import { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CatalogTable, catalogPlugin } from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  EntityKindPicker,
  EntityListProvider,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Content,
  CreateButton,
  PageWithHeader,
  SupportButton,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { Box } from '@material-ui/core';
import { serviceColumns } from './columns';
import { FilterBar } from './FilterBar';

const TABLE_OPTIONS = {
  search: false,
  paging: true,
  pageSize: 25,
  pageSizeOptions: [25, 50, 100],
  padding: 'dense' as const,
};

/**
 * On first load, where to send the browser so the URL names the services type.
 * The Type filter reads its value from the URL, so this keeps it showing what
 * the list is filtered to. Later changes, including "all", are left alone.
 */
const useServicesRedirect = () => {
  const { pathname, search } = useLocation();
  const checked = useRef(false);
  if (checked.current) return undefined;
  checked.current = true;
  const params = new URLSearchParams(search);
  if (params.has('filters[type]')) return undefined;
  params.set('filters[type]', 'service');
  return `${pathname}?${params.toString()}`;
};

/** The table, titled by what it is filtered to ("Services (106)"). */
const Results = () => {
  const { entities, filters } = useEntityList();
  const types = filters.type?.getTypes() ?? [];
  const noun = types.length === 1 ? `${types[0]}s` : 'components';
  const title = `${noun[0].toUpperCase()}${noun.slice(1)} (${entities.length})`;
  return (
    <CatalogTable
      title={title}
      columns={serviceColumns}
      actions={[]}
      tableOptions={TABLE_OPTIONS}
    />
  );
};

/** The catalog home: one filter bar above a short table, in place of the stock filter column. */
export const ServicesCatalogPage = () => {
  const createLink = useRouteRef(catalogPlugin.externalRoutes.createComponent);
  const { allowed } = usePermission({
    permission: catalogEntityCreatePermission,
  });
  const redirectTo = useServicesRedirect();
  if (redirectTo) return <Navigate replace to={redirectTo} />;

  const actions = (
    <>
      {allowed && (
        <CreateButton title="Create" to={createLink && createLink()} />
      )}
      <SupportButton>Find the services and tools ATG runs.</SupportButton>
    </>
  );

  return (
    <EntityListProvider>
      <PageWithHeader title="ATG Catalog" themeId="home">
        <Content>
          <EntityKindPicker initialFilter="component" hidden />
          <FilterBar actions={actions} />
          <Box style={{ overflowX: 'auto' }}>
            <Results />
          </Box>
        </Content>
      </PageWithHeader>
    </EntityListProvider>
  );
};
